import { useState, useEffect } from "react";
import axios from "axios";
import { ConfigContext } from "./context";

const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT || "";
const API_CONFIG_ENDPOINT = `${API_ENDPOINT}/config`;
const MAX_RETRIES = 3;
const RETRY_DELAY = 2000;

function validateConfig(config) {
  const errors = [];

  if (!config.apiEp) {
    errors.push("API endpoint is missing");
  }

  if (!config.auth0) {
    errors.push("Auth0 configuration is missing");
  } else {
    if (!config.auth0.domain) errors.push("Auth0 domain is missing");
    if (!config.auth0.clientId) errors.push("Auth0 clientId is missing");
    if (!config.auth0.audience) errors.push("Auth0 audience is missing");
  }

  return errors;
}

export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(API_CONFIG_ENDPOINT, { timeout: 10000 });
      // console.log(response.data);
      const configData = { ...response.data, apiEp: API_ENDPOINT };

      const validationErrors = validateConfig(configData);
      if (validationErrors.length > 0) {
        throw new Error(
          `Invalid configuration: ${validationErrors.join(", ")}`
        );
      }

      setConfig(configData);
      setLoading(false);
      setRetryCount(0);
    } catch (err) {
      console.error("Failed to fetch config:", err);

      if (retryCount < MAX_RETRIES) {
        console.log(`Retrying... (${retryCount + 1}/${MAX_RETRIES})`);
        setTimeout(() => {
          setRetryCount(retryCount + 1);
        }, RETRY_DELAY);
      } else {
        setError({
          message: "Failed to load application configuration",
          canRetry: true,
        });
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryCount]);

  const retryConfig = () => {
    setRetryCount(0);
    fetchConfig();
  };

  return (
    <ConfigContext.Provider value={{ ...config, loading, error, retryConfig }}>
      {children}
    </ConfigContext.Provider>
  );
};
